// Minimals.cc inspired theme configuration
export const themeConfig = {
  defaultMode: 'light',
  cssVariables: true,
  direction: 'ltr',
};

// Primary color palette based on Minimals.cc
export const palette = {
  light: {
    primary: {
      lighter: '#C8FAD6',
      light: '#5BE49B', 
      main: '#00A76F',
      dark: '#007867',
      darker: '#004B50',
      contrastText: '#FFFFFF'
    },
    secondary: {
      lighter: '#D6E4FF',
      light: '#84A9FF',
      main: '#3366FF',
      dark: '#1939B7',
      darker: '#091A7A',
      contrastText: '#FFFFFF'
    },
    info: {
      lighter: '#CAFDF5',
      light: '#61F3F3',
      main: '#00B8D9',
      dark: '#006C9C',
      darker: '#003768',
      contrastText: '#FFFFFF'
    },
    success: {
      lighter: '#D8FBDE',
      light: '#86E8AB',
      main: '#36B37E',
      dark: '#1B806A',
      darker: '#0A5554',
      contrastText: '#FFFFFF'
    },
    warning: {
      lighter: '#FFF5CC',
      light: '#FFD666',
      main: '#FFAB00',
      dark: '#B76E00',
      darker: '#7A4100',
      contrastText: '#212B36'
    },
    error: {
      lighter: '#FFE9D5',
      light: '#FFAC82',
      main: '#FF5630',
      dark: '#B71D18',
      darker: '#7A0916',
      contrastText: '#FFFFFF'
    },
    grey: {
      0: '#FFFFFF',
      100: '#F9FAFB',
      200: '#F4F6F8',
      300: '#DFE3E8',
      400: '#C4CDD5',
      500: '#919EAB',
      600: '#637381',
      700: '#454F5B',
      800: '#212B36',
      900: '#161C24',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
      neutral: '#F4F6F8',
    },
    text: {
      primary: '#212B36',
      secondary: '#637381',
      disabled: '#919EAB',
    },
    divider: '#F2F4F7',
    action: {
      hover: 'rgba(145, 158, 171, 0.08)',
      selected: 'rgba(145, 158, 171, 0.12)',
      disabled: 'rgba(145, 158, 171, 0.8)',
      disabledBackground: 'rgba(145, 158, 171, 0.24)',
      focus: 'rgba(145, 158, 171, 0.24)',
    }
  },
  dark: {
    primary: {
      lighter: '#C8FAD6',
      light: '#5BE49B',
      main: '#00A76F',
      dark: '#007867', 
      darker: '#004B50',
      contrastText: '#FFFFFF'
    },
    secondary: {
      lighter: '#D6E4FF',
      light: '#84A9FF',
      main: '#3366FF',
      dark: '#1939B7',
      darker: '#091A7A',
      contrastText: '#FFFFFF'
    },
    info: {
      lighter: '#CAFDF5',
      light: '#61F3F3',
      main: '#00B8D9',
      dark: '#006C9C',
      darker: '#003768',
      contrastText: '#FFFFFF'
    },
    success: {
      lighter: '#D8FBDE',
      light: '#86E8AB',
      main: '#36B37E',
      dark: '#1B806A',
      darker: '#0A5554',
      contrastText: '#FFFFFF'
    },
    warning: {
      lighter: '#FFF5CC',
      light: '#FFD666',
      main: '#FFAB00',
      dark: '#B76E00',
      darker: '#7A4100',
      contrastText: '#212B36'
    },
    error: {
      lighter: '#FFE9D5',
      light: '#FFAC82',
      main: '#FF5630',
      dark: '#B71D18',
      darker: '#7A0916',
      contrastText: '#FFFFFF'
    },
    grey: {
      0: '#FFFFFF',
      100: '#F9FAFB',
      200: '#F4F6F8',
      300: '#DFE3E8',
      400: '#C4CDD5',
      500: '#919EAB',
      600: '#637381',
      700: '#454F5B',
      800: '#212B36',
      900: '#161C24',
    },
    background: {
      default: '#161C24',
      paper: '#212B36',
      neutral: '#454F5B',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#919EAB',
      disabled: '#637381',
    },
    divider: 'rgba(145, 158, 171, 0.2)',
    action: {
      hover: 'rgba(145, 158, 171, 0.08)',
      selected: 'rgba(145, 158, 171, 0.16)',
      disabled: 'rgba(145, 158, 171, 0.8)',
      disabledBackground: 'rgba(145, 158, 171, 0.24)',
      focus: 'rgba(145, 158, 171, 0.24)',
    }
  }
};

// Typography based on Minimals.cc
export const typography = {
  fontFamily: '"Public Sans", sans-serif',
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: {
    fontWeight: 800,
    lineHeight: 80 / 64,
    fontSize: '2.5rem', // 40px
    letterSpacing: 0,
  },
  h2: {
    fontWeight: 800,
    lineHeight: 64 / 48,
    fontSize: '2rem', // 32px
    letterSpacing: 0,
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: '1.5rem', // 24px
    letterSpacing: 0,
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: '1.25rem', // 20px
    letterSpacing: 0,
  },
  h5: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: '1.125rem', // 18px
    letterSpacing: 0,
  },
  h6: {
    fontWeight: 600,
    lineHeight: 28 / 18,
    fontSize: '1.125rem', // 18px
    letterSpacing: 0,
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: '1rem', // 16px
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: '0.875rem', // 14px
  },
  body1: {
    lineHeight: 1.5,
    fontSize: '1rem', // 16px
  },
  body2: {
    lineHeight: 22 / 14,
    fontSize: '0.875rem', // 14px
  },
  caption: {
    lineHeight: 1.5,
    fontSize: '0.75rem', // 12px
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: '0.75rem', // 12px
    textTransform: 'uppercase' as const,
  },
  button: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: '0.875rem', // 14px
    textTransform: 'unset' as const,
  },
};

// Custom shadows based on Minimals.cc
export const shadows = {
  light: [
    'none',
    '0px 2px 1px -1px rgba(145, 158, 171, 0.2), 0px 1px 1px 0px rgba(145, 158, 171, 0.14), 0px 1px 3px 0px rgba(145, 158, 171, 0.12)',
    '0px 3px 1px -2px rgba(145, 158, 171, 0.2), 0px 2px 2px 0px rgba(145, 158, 171, 0.14), 0px 1px 5px 0px rgba(145, 158, 171, 0.12)',
    '0px 3px 3px -2px rgba(145, 158, 171, 0.2), 0px 3px 4px 0px rgba(145, 158, 171, 0.14), 0px 1px 8px 0px rgba(145, 158, 171, 0.12)',
    '0px 2px 4px -1px rgba(145, 158, 171, 0.2), 0px 4px 5px 0px rgba(145, 158, 171, 0.14), 0px 1px 10px 0px rgba(145, 158, 171, 0.12)',
    '0px 3px 5px -1px rgba(145, 158, 171, 0.2), 0px 5px 8px 0px rgba(145, 158, 171, 0.14), 0px 1px 14px 0px rgba(145, 158, 171, 0.12)',
    '0px 3px 5px -1px rgba(145, 158, 171, 0.2), 0px 6px 10px 0px rgba(145, 158, 171, 0.14), 0px 1px 18px 0px rgba(145, 158, 171, 0.12)',
    '0px 4px 5px -2px rgba(145, 158, 171, 0.2), 0px 7px 10px 1px rgba(145, 158, 171, 0.14), 0px 2px 16px 1px rgba(145, 158, 171, 0.12)',
    '0px 5px 5px -3px rgba(145, 158, 171, 0.2), 0px 8px 10px 1px rgba(145, 158, 171, 0.14), 0px 3px 14px 2px rgba(145, 158, 171, 0.12)',
    '0px 5px 6px -3px rgba(145, 158, 171, 0.2), 0px 9px 12px 1px rgba(145, 158, 171, 0.14), 0px 3px 16px 2px rgba(145, 158, 171, 0.12)',
    '0px 6px 6px -3px rgba(145, 158, 171, 0.2), 0px 10px 14px 1px rgba(145, 158, 171, 0.14), 0px 4px 18px 3px rgba(145, 158, 171, 0.12)',
    '0px 6px 7px -4px rgba(145, 158, 171, 0.2), 0px 11px 15px 1px rgba(145, 158, 171, 0.14), 0px 4px 20px 3px rgba(145, 158, 171, 0.12)',
    '0px 7px 8px -4px rgba(145, 158, 171, 0.2), 0px 12px 17px 2px rgba(145, 158, 171, 0.14), 0px 5px 22px 4px rgba(145, 158, 171, 0.12)',
    '0px 7px 8px -4px rgba(145, 158, 171, 0.2), 0px 13px 19px 2px rgba(145, 158, 171, 0.14), 0px 5px 24px 4px rgba(145, 158, 171, 0.12)',
    '0px 7px 9px -4px rgba(145, 158, 171, 0.2), 0px 14px 21px 2px rgba(145, 158, 171, 0.14), 0px 5px 26px 4px rgba(145, 158, 171, 0.12)',
    '0px 8px 9px -5px rgba(145, 158, 171, 0.2), 0px 15px 22px 2px rgba(145, 158, 171, 0.14), 0px 6px 28px 5px rgba(145, 158, 171, 0.12)',
    '0px 8px 10px -5px rgba(145, 158, 171, 0.2), 0px 16px 24px 2px rgba(145, 158, 171, 0.14), 0px 6px 30px 5px rgba(145, 158, 171, 0.12)',
    '0px 8px 11px -5px rgba(145, 158, 171, 0.2), 0px 17px 26px 2px rgba(145, 158, 171, 0.14), 0px 6px 32px 5px rgba(145, 158, 171, 0.12)',
    '0px 9px 11px -5px rgba(145, 158, 171, 0.2), 0px 18px 28px 2px rgba(145, 158, 171, 0.14), 0px 7px 34px 6px rgba(145, 158, 171, 0.12)',
    '0px 9px 12px -6px rgba(145, 158, 171, 0.2), 0px 19px 29px 2px rgba(145, 158, 171, 0.14), 0px 7px 36px 6px rgba(145, 158, 171, 0.12)',
    '0px 10px 13px -6px rgba(145, 158, 171, 0.2), 0px 20px 31px 3px rgba(145, 158, 171, 0.14), 0px 8px 38px 7px rgba(145, 158, 171, 0.12)',
    '0px 10px 13px -6px rgba(145, 158, 171, 0.2), 0px 21px 33px 3px rgba(145, 158, 171, 0.14), 0px 8px 40px 7px rgba(145, 158, 171, 0.12)',
    '0px 10px 14px -6px rgba(145, 158, 171, 0.2), 0px 22px 35px 3px rgba(145, 158, 171, 0.14), 0px 8px 42px 7px rgba(145, 158, 171, 0.12)',
    '0px 11px 14px -7px rgba(145, 158, 171, 0.2), 0px 23px 36px 3px rgba(145, 158, 171, 0.14), 0px 9px 44px 8px rgba(145, 158, 171, 0.12)',
    '0px 11px 15px -7px rgba(145, 158, 171, 0.2), 0px 24px 38px 3px rgba(145, 158, 171, 0.14), 0px 9px 46px 8px rgba(145, 158, 171, 0.12)'
  ],
  dark: [
    'none',
    '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
    '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
    '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
    '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
    '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)',
    '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
    '0px 4px 5px -2px rgba(0, 0, 0, 0.2), 0px 7px 10px 1px rgba(0, 0, 0, 0.14), 0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    '0px 5px 6px -3px rgba(0, 0, 0, 0.2), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 3px 16px 2px rgba(0, 0, 0, 0.12)',
    '0px 6px 6px -3px rgba(0, 0, 0, 0.2), 0px 10px 14px 1px rgba(0, 0, 0, 0.14), 0px 4px 18px 3px rgba(0, 0, 0, 0.12)',
    '0px 6px 7px -4px rgba(0, 0, 0, 0.2), 0px 11px 15px 1px rgba(0, 0, 0, 0.14), 0px 4px 20px 3px rgba(0, 0, 0, 0.12)',
    '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(0, 0, 0, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
    '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 13px 19px 2px rgba(0, 0, 0, 0.14), 0px 5px 24px 4px rgba(0, 0, 0, 0.12)',
    '0px 7px 9px -4px rgba(0, 0, 0, 0.2), 0px 14px 21px 2px rgba(0, 0, 0, 0.14), 0px 5px 26px 4px rgba(0, 0, 0, 0.12)',
    '0px 8px 9px -5px rgba(0, 0, 0, 0.2), 0px 15px 22px 2px rgba(0, 0, 0, 0.14), 0px 6px 28px 5px rgba(0, 0, 0, 0.12)',
    '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
    '0px 8px 11px -5px rgba(0, 0, 0, 0.2), 0px 17px 26px 2px rgba(0, 0, 0, 0.14), 0px 6px 32px 5px rgba(0, 0, 0, 0.12)',
    '0px 9px 11px -5px rgba(0, 0, 0, 0.2), 0px 18px 28px 2px rgba(0, 0, 0, 0.14), 0px 7px 34px 6px rgba(0, 0, 0, 0.12)',
    '0px 9px 12px -6px rgba(0, 0, 0, 0.2), 0px 19px 29px 2px rgba(0, 0, 0, 0.14), 0px 7px 36px 6px rgba(0, 0, 0, 0.12)',
    '0px 10px 13px -6px rgba(0, 0, 0, 0.2), 0px 20px 31px 3px rgba(0, 0, 0, 0.14), 0px 8px 38px 7px rgba(0, 0, 0, 0.12)',
    '0px 10px 13px -6px rgba(0, 0, 0, 0.2), 0px 21px 33px 3px rgba(0, 0, 0, 0.14), 0px 8px 40px 7px rgba(0, 0, 0, 0.12)',
    '0px 10px 14px -6px rgba(0, 0, 0, 0.2), 0px 22px 35px 3px rgba(0, 0, 0, 0.14), 0px 8px 42px 7px rgba(0, 0, 0, 0.12)',
    '0px 11px 14px -7px rgba(0, 0, 0, 0.2), 0px 23px 36px 3px rgba(0, 0, 0, 0.14), 0px 9px 44px 8px rgba(0, 0, 0, 0.12)',
    '0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)'
  ]
};

// Custom shadows for cards and overlays
export const customShadows = {
  light: {
    z1: '0 1px 2px 0 rgba(145, 158, 171, 0.08)',
    z4: '0 4px 8px 0 rgba(145, 158, 171, 0.12)',
    z8: '0 8px 16px 0 rgba(145, 158, 171, 0.16)',
    z12: '0 12px 24px -4px rgba(145, 158, 171, 0.12)',
    z16: '0 16px 32px -4px rgba(145, 158, 171, 0.16)',
    z20: '0 20px 40px -4px rgba(145, 158, 171, 0.20)',
    z24: '0 24px 48px 0 rgba(145, 158, 171, 0.20)',
    card: '0 0 2px 0 rgba(145, 158, 171, 0.08), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
    dropdown: '0 0 2px 0 rgba(145, 158, 171, 0.24), -20px 20px 40px -4px rgba(145, 158, 171, 0.24)',
    dialog: '-40px 40px 80px -8px rgba(145, 158, 171, 0.24)',
    primary: '0 8px 16px 0 rgba(0, 167, 111, 0.24)',
    info: '0 8px 16px 0 rgba(0, 184, 217, 0.24)',
    secondary: '0 8px 16px 0 rgba(51, 102, 255, 0.24)',
    success: '0 8px 16px 0 rgba(54, 179, 126, 0.24)',
    warning: '0 8px 16px 0 rgba(255, 171, 0, 0.24)',
    error: '0 8px 16px 0 rgba(255, 86, 48, 0.24)',
  },
  dark: {
    z1: '0 1px 2px 0 rgba(0, 0, 0, 0.16)',
    z4: '0 4px 8px 0 rgba(0, 0, 0, 0.24)',
    z8: '0 8px 16px 0 rgba(0, 0, 0, 0.24)',
    z12: '0 12px 24px -4px rgba(0, 0, 0, 0.24)',
    z16: '0 16px 32px -4px rgba(0, 0, 0, 0.24)',
    z20: '0 20px 40px -4px rgba(0, 0, 0, 0.28)',
    z24: '0 24px 48px 0 rgba(0, 0, 0, 0.28)',
    card: '0 0 2px 0 rgba(0, 0, 0, 0.2), 0 12px 24px -4px rgba(0, 0, 0, 0.12)',
    dropdown: '0 0 2px 0 rgba(0, 0, 0, 0.24), -20px 20px 40px -4px rgba(0, 0, 0, 0.24)',
    dialog: '-40px 40px 80px -8px rgba(0, 0, 0, 0.24)',
    primary: '0 8px 16px 0 rgba(0, 167, 111, 0.24)',
    info: '0 8px 16px 0 rgba(0, 184, 217, 0.24)',
    secondary: '0 8px 16px 0 rgba(51, 102, 255, 0.24)',
    success: '0 8px 16px 0 rgba(54, 179, 126, 0.24)',
    warning: '0 8px 16px 0 rgba(255, 171, 0, 0.24)',
    error: '0 8px 16px 0 rgba(255, 86, 48, 0.24)',
  }
}; 